import { APIClient } from "../helpers/api_helper";
import { ApiResponse, PaginatedApiResponse, PaginationParams } from "../types/api";

const api = new APIClient();

export type ClinicalProtocolStatus = "active" | "completed" | "cancelled";
export type ClinicalProtocolSessionStatus = "planned" | "done" | "skipped";

export interface ClinicalProtocolLinkedCredit {
  id: number;
  service_id: number;
  sessions_total: number;
  sessions_remaining: number;
  expires_at: string | null;
  source?: string;
  service?: { id: number; name: string } | null;
  service_package?: { id: number; name: string } | null;
}

export interface ClinicalProtocolCreditPreview {
  can_consume: boolean;
  sessions_remaining: number;
  label: string | null;
  client_credit_id: number | null;
  service_id: number | null;
}

export interface ClinicalProtocolSession {
  id: number;
  clinical_protocol_id: number;
  session_number: number;
  status: ClinicalProtocolSessionStatus;
  appointment_id: number | null;
  client_record_id: number | null;
  client_credit_id?: number | null;
  credit_transaction_id?: number | null;
  credit_consumed?: boolean;
  completed_at: string | null;
  notes: string | null;
  appointment?: {
    id: number;
    start_datetime: string | null;
    status: string;
  } | null;
  client_record?: {
    id: number;
    record_type: string;
    appointment_id: number | null;
    created_at: string | null;
  } | null;
  created_at?: string;
  updated_at?: string;
}

export interface ClinicalProtocol {
  id: number;
  tenant_id: number;
  client_id: number;
  name: string;
  service_id: number | null;
  template_id: number | null;
  client_credit_id?: number | null;
  total_sessions: number;
  status: ClinicalProtocolStatus;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  notes: string | null;
  created_by_user_id: number | null;
  sessions_done: number;
  sessions_skipped: number;
  sessions_completed_count: number;
  current_session_number: number;
  progress_label: string;
  service?: { id: number; name: string } | null;
  template?: { id: number; title: string } | null;
  linked_credit?: ClinicalProtocolLinkedCredit | null;
  credit_preview?: ClinicalProtocolCreditPreview | null;
  sessions: ClinicalProtocolSession[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateClinicalProtocolPayload {
  name: string;
  total_sessions: number;
  service_id?: number | null;
  template_id?: number | null;
  client_credit_id?: number | null;
  notes?: string | null;
  started_at?: string | null;
}

export interface UpdateClinicalProtocolPayload {
  name?: string;
  notes?: string | null;
  service_id?: number | null;
  template_id?: number | null;
  client_credit_id?: number | null;
  status?: "cancelled";
}

export interface UpdateClinicalProtocolSessionPayload {
  status?: ClinicalProtocolSessionStatus;
  appointment_id?: number | null;
  client_record_id?: number | null;
  notes?: string | null;
  consume_credit?: boolean;
}

export const listClientClinicalProtocols = (
  clientId: number,
  params: PaginationParams & { status?: ClinicalProtocolStatus } = {}
) =>
  api.get<PaginatedApiResponse<ClinicalProtocol>>(
    `/clients/${clientId}/clinical-protocols`,
    params
  );

export const createClientClinicalProtocol = (
  clientId: number,
  payload: CreateClinicalProtocolPayload
): Promise<ApiResponse<ClinicalProtocol>> =>
  api.create<ApiResponse<ClinicalProtocol>>(
    `/clients/${clientId}/clinical-protocols`,
    payload
  );

export const getClinicalProtocol = (
  protocolId: number
): Promise<ApiResponse<ClinicalProtocol>> =>
  api.get<ApiResponse<ClinicalProtocol>>(`/clinical-protocols/${protocolId}`);

export const updateClinicalProtocol = (
  protocolId: number,
  payload: UpdateClinicalProtocolPayload
): Promise<ApiResponse<ClinicalProtocol>> =>
  api.update<ApiResponse<ClinicalProtocol>>(
    `/clinical-protocols/${protocolId}`,
    payload
  );

export const updateClinicalProtocolSession = (
  protocolId: number,
  sessionId: number,
  payload: UpdateClinicalProtocolSessionPayload
): Promise<
  ApiResponse<{ session: ClinicalProtocolSession; protocol: ClinicalProtocol }>
> =>
  api.update<
    ApiResponse<{ session: ClinicalProtocolSession; protocol: ClinicalProtocol }>
  >(`/clinical-protocols/${protocolId}/sessions/${sessionId}`, payload);
